import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import { BUSINESS, IMAGES } from "../lib/site";

const FAILURES = [
  "Lighting and signalling - blown bulbs, misaligned headlamps and faulty indicators",
  "Tyres and wheels - tread below the legal limit, damage and incorrect fitment",
  "Brakes - worn pads and discs, imbalance and handbrake performance",
  "Suspension - worn bushes, ball joints and shock absorbers",
  "Visibility - worn wipers, washer faults and windscreen damage",
  "Exhaust and emissions - leaks, corrosion and emissions failures",
];

export default function MotPage() {
  return (
    <>
      <SEO
        title="Class 4 MOT Testing in Adlington, Chorley £45 | PR7 MOT Centre"
        description="MOT testing in Adlington from £45. Class 4 MOT centre serving Adlington, Chorley and Lancashire. Fast appointments, clear results and a full retest within 10 working days for £45. Call 01257 786 540."
        path="/mot-testing-adlington"
      />

      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 eng-grid" />
        <div className="container-pr7 relative py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow-light mb-3">MOT Testing in Adlington</p>
            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Class 4 MOT testing from <span className="text-sky">£45</span>
            </h1>
            <p className="text-pale/75 text-base sm:text-lg leading-relaxed mt-5 max-w-xl">
              PR7 MOT Centre provides professional Class 4 MOT testing for drivers across Adlington, Chorley and the surrounding Lancashire area - with straightforward pricing and clear, honest results.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Link to="/booking?service=MOT" data-testid="mot-book-btn" className="btn-primary">BOOK AN MOT <ArrowRight size={17} /></Link>
              <a href={BUSINESS.phoneHref} data-testid="mot-call-btn" className="btn-outline-light">CALL {BUSINESS.phone}</a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={IMAGES.lift} alt="Vehicle on the ramp for MOT testing at our Adlington MOT centre" className="rounded-lg object-cover w-full h-72 lg:h-96 shadow-2xl" loading="eager" />
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-pr7 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl tracking-tight">What is an MOT?</h2>
            <p className="text-muted-foreground leading-relaxed mt-4">
              An MOT is an annual legal requirement for most vehicles over three years old in the UK. It checks that your vehicle meets minimum road safety and environmental standards at the time of the test. A Class 4 MOT covers most cars, small vans and passenger vehicles with up to 8 seats.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              At our MOT centre in Adlington, your vehicle is inspected by an experienced tester and you receive a clear pass or fail result with an explanation of anything that needs attention.
            </p>
            <h2 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl tracking-tight mt-10">Common reasons for MOT failures</h2>
            <ul className="mt-5 space-y-3">
              {FAILURES.map((f) => (
                <li key={f} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                  <CheckCircle2 size={17} className="text-brand shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="bg-pale border border-brand/20 rounded-lg p-7">
              <h2 className="font-heading font-extrabold text-ink text-xl tracking-tight">If your vehicle fails</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                If your vehicle fails its MOT, we explain exactly why and what is needed to put it right. There is no obligation to have repairs done with us, and we will never carry out work without your agreement.
              </p>
              <div className="mt-5 bg-white border border-border rounded p-5">
                <p className="font-heading font-bold text-ink text-sm tracking-wide">RETEST INFORMATION</p>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  A full retest within <strong className="text-ink">10 working days</strong> is charged at{" "}
                  <span className="font-mono font-semibold text-brand">£45</span>.
                </p>
              </div>
            </div>
            <div className="bg-navy rounded-lg p-7 mt-6 relative overflow-hidden">
              <div className="absolute inset-0 eng-grid" />
              <div className="relative">
                <h2 className="font-heading font-extrabold text-white text-xl tracking-tight">How to book your MOT</h2>
                <p className="text-sm text-pale/70 leading-relaxed mt-3">
                  Book online using our booking form, call <a href={BUSINESS.phoneHref} className="text-sky font-semibold">{BUSINESS.phone}</a>, or email{" "}
                  <a href={`mailto:${BUSINESS.email}`} className="text-sky font-semibold">{BUSINESS.email}</a>. Bring your vehicle to Margaret House, Huyton Road, Adlington, PR7 4HD at your confirmed time.
                </p>
                <Link to="/booking?service=MOT" data-testid="mot-book-bottom-btn" className="btn-primary mt-6">BOOK AN MOT <ArrowRight size={16} /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-surface py-12">
        <div className="container-pr7 text-center">
          <p className="text-sm text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Looking for an MOT in Adlington or an MOT in Chorley? PR7 MOT Centre is your local Class 4 MOT centre in Lancashire, based at Margaret House, Huyton Road, Adlington, PR7 4HD - easy to reach from Chorley, Horwich, Blackrod and surrounding areas.
          </p>
        </div>
      </section>
    </>
  );
}
